/* eslint-disable no-unsafe-optional-chaining */
import { useMutation, useQueries } from "@tanstack/react-query";
import { ModelPortfolioObjectInterface } from "interfaces/EquityBasket";
import { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { GET_ALL_PORTFOLIO, GET_HOLDING, INDICES } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { exportArrayBufferToExcel } from "utils/exportCsv";
import { QueryKeys } from "utils/queryKey";
import { PortfolioDropdownInterface } from "utils/types";
import useGenerateQueryParams from "./useGenerateQueryParams";
import { useFetchQuery } from "./useQueries";

export const usePortfolioSummary = ({
  selectedModelPortfolio,
}: {
  selectedModelPortfolio?: ModelPortfolioObjectInterface;
}) => {
  const { id, modelPortfolioId: modelId } = useParams();

  const modelPortfolioId = selectedModelPortfolio?.id || modelId;

  const [selectedCriteria, setSelectedCriteria] =
    useState<string>("portfolioValue");

  const navigate = useNavigate();

  const [linechartTabs, setLinechartTabs] = useState(0);

  const [portfolioList, setPortfolioList] = useState([]);

  const [pageScrollTabs, setpageScrollTabs] = useState(0);

  const [partnerSelection, setPartnerSelection] = useState([]);

  const { mutate: downloadNavData, isLoading: downloadLoading } = useMutation({
    mutationKey: ["DOWNLOAD_NAV_DATA"],
    mutationFn: () =>
      axiosInstance.get(
        `/model_portfolio/${id}/version/${modelPortfolioId}/download_nav/`,
        {
          responseType: "blob",
        },
      ),
    onSuccess: (response) => {
      const baseName = selectedModelPortfolio?.version || "NAV";
      const fileName = `${baseName}_performance_comparison`;
      exportArrayBufferToExcel(response.data, fileName);
    },
    onError: () => {
      toast.error("Failed to download portfolio NAV data.");
    },
  });

  const [selectedItems, setSelectedItems] = useState<any[]>(
    modelPortfolioId || id
      ? [
          {
            id: Number(modelPortfolioId || id),
            value: Number(modelPortfolioId || id),
            name: "",
            code: "",
            type: "portfolio",
            portfolios: {
              id: Number(modelPortfolioId || id),
              value: Number(modelPortfolioId || id),
              name: "",
              portfolioId: "",
            },
          },
        ]
      : [],
  );

  /** ============================================================================ */
  const handleRowClickApiCall = async (id: string) => {
    navigate(`/app/insights-holdings/${id}/details`, {
      state: {
        portfoliosIds: selectedItems
          ?.map((data: any) => data?.portfolios)
          ?.flat()
          ?.map((dataItem) => dataItem?.value),
      },
    });
  };

  /** ============================================================================ */
  const [
    { data: lineChartData, isFetching: isMainChartLoading },
    { data: AUMData, isFetching: aumLoading },
  ] = useQueries({
    queries: [
      {
        queryKey: [
          selectedItems,
          partnerSelection,
          QueryKeys.HOLDINGS_SUMMARY_LINE_CHART_DATA,
        ],
        retry: 0,
        queryFn: () =>
          axiosInstance.post(`/aums/timeline/portfolios/portfolio_summary/`, {
            selected_portfolios: selectedItems
              ?.map((data: any) => data?.portfolios)
              ?.flat()
              ?.map((dataItem) => dataItem?.value),
            is_model_portfolio: !!modelPortfolioId,
            partners: partnerSelection,
          }),
        keepPreviousData: true,
      },
      {
        queryKey: [selectedItems, partnerSelection, "CASH_EQUITY"],
        retry: 0,
        queryFn: () =>
          axiosInstance.post(`/holdings/cash_equity/`, {
            portfolios: selectedItems
              ?.map((data: any) => data?.portfolios)
              ?.flat()
              ?.map((dataItem) => dataItem?.value),
            is_model_portfolio: !!modelPortfolioId,
            partners: partnerSelection,
          }),
      },
    ],
  });

  /** ============================================================================ */
  const { isFetching: portfolioLoading } = useFetchQuery({
    key: [QueryKeys.PORTFOLIO_OVERVIEW],
    retry: 0,
    route: GET_ALL_PORTFOLIO,
    enabled: !modelPortfolioId,
    success: (response: any) => {
      if (response?.data?.length === 0) {
        setSelectedItems(
          response?.data?.map((item: PortfolioDropdownInterface) => ({
            name: `${item?.name} - ${item?.code}`,
            value: item?.id,
            type: item?.type,
            portfolios: item?.portfolios?.map((portfolio) => ({
              label: portfolio?.name,
              value: portfolio?.id,
              portfolioId: portfolio?.portfolioId,
            })),
          })),
        );
      }

      setPortfolioList(
        response?.data?.map((item: PortfolioDropdownInterface) => ({
          name: `${item?.name} - ${item?.code}`,
          value: item?.id,
          type: item?.type,
          clientName: item?.clientName,
          portfolios: item?.portfolios?.map((portfolio) => ({
            label: portfolio?.name,
            value: portfolio?.id,
            portfolioId: portfolio?.portfolioId,
          })),
        })),
      );
    },
  });
  /** ============================================================================ */
  const { data: niftyData, isFetching: niftyLoading } = useFetchQuery({
    key: [QueryKeys.NIFTY_SENSEX],
    retry: 0,
    route: INDICES,
    keepPreviousData: true,
  });

  const requestBody = useGenerateQueryParams("holdingSummary", {
    is_model_portfolio: !!modelPortfolioId,
    portfolios: JSON.stringify(
      selectedItems
        ?.map((data: any) => data?.portfolios)
        ?.flat()
        ?.map((dataItem) => dataItem?.value),
    ),
    partners: JSON.stringify(partnerSelection),
  });

  const { data: holdingData, isFetching: holdingLoading } = useFetchQuery({
    key: [QueryKeys.HOLDING_SUMMARY, requestBody],
    retry: 0,
    route: GET_HOLDING,
    requestBody,
    enabled: !window.location.href.includes("equity-basket"),
  });

  /** ============================================================================ */
  const loading =
    portfolioLoading ||
    isMainChartLoading ||
    aumLoading ||
    niftyLoading ||
    holdingLoading;

  return {
    setSelectedItems,
    selectedItems,
    selectedCriteria,
    setSelectedCriteria,
    lineChartData,
    handleRowClickApiCall,
    AUMData,
    loading,
    niftyData,
    portfolioList,
    holdingData,
    linechartTabs,
    setLinechartTabs,
    id,
    pageScrollTabs,
    setpageScrollTabs,
    partnerSelection,
    setPartnerSelection,
    modelPortfolioId,
    downloadNavData,
    downloadLoading,
  };
};
